import React, { useRef, useState } from 'react'
import { Row, Button } from '@fs/zion-ui'
import { useFeatureFlags } from '@fs/zion-flags'
import { NoticeLoading } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { useZionFormContext } from '@fs/zion-form'
import { trackEvent } from '@fs/zion-analytics'
import { saveSlsAndUpdateState, useSlsDispatchContext, useSlsHighlightsContext } from '@fs/zion-sls'
import { errorCollector } from '../../../../../shared/utils/utils'
import ErrorOverlay from '../../../../../shared/components/errorOverlay/ErrorOverlay'
import UnsavedChangesOverlay from './UnsavedChangesOverlay'
import { headers } from '../../../../../shared/services/config'

export default function SaveComponent({
  finished = () => {},
  canceled = () => {},
  onError = () => {},
  saveChangesOverlay,
  formName,
  onSubmit,
  disableSave,
  personId,
  appName,
  shouldSavetoSls = true,
}) {
  const { rip_sharedImageViewer_saveChanges: saveChangesFlag } = useFeatureFlags(['rip_sharedImageViewer_saveChanges'])
  const dispatch = useSlsDispatchContext()
  const [t] = useTranslation()
  const h = useSlsHighlightsContext()
  const { commitEdits } = h
  const saveButtonRef = useRef(null)
  const { formState, getValues } = useZionFormContext()
  const { isValid } = formState
  const [error, setError] = useState()
  const [saving, setSaving] = useState(false)

  const handleSubmitClick = async () => {
    if (!isValid) return

    if (formName)
      trackEvent({
        event_name: `${formName.replaceAll('-', '_').replace('edit_', '')}_edit`,
        link_name: `Edit: ${formName
          .replace('edit-', '')
          .split('-')
          .map((word) => word.replace(word[0], word[0].toUpperCase()))
          .join(' ')}: Save`,
      })

    setSaving(true)
    const formValues = getValues()
    const { sls, dispatches } = onSubmit(formValues)

    const successHandler = () => {
      setSaving(false)
      finished(dispatches, sls) // This will return the dispatches and SLS dispatches to the calling component
      if (shouldSavetoSls) {
        // Update static copy of highlights so these changes will propagate through future edits
        commitEdits()
      } // Changes on highlights are already staged and sent to SLS, so we need to clear them
      saveChangesOverlay.close()
    }

    if (shouldSavetoSls) {
      await saveSlsAndUpdateState({
        sls,
        dispatches,
        saveChangesFlag,
        userId: personId,
        dispatch,
        appName,
        fsUserAgentChain: headers['FS-User-Agent-Chain'],
        successHandler,
        errorHandler: (err) => {
          setError(err)
          errorCollector(err, null, {
            codeId: 99,
            sls,
            status: err?.response?.status,
            responseBody: err?.response?.data,
          })
          setSaving(false)
          onError(err)
        },
      })
    } else {
      successHandler()
    }
  }

  return (
    <>
      <Row alignX="end" gutters="md">
        <Button
          data-testid="save-component-cancel-button"
          linkName="PanelFooterCancelButton"
          form={formName}
          disabled={saving}
          onClick={() => {
            if (formState?.isDirty) {
              saveChangesOverlay.open()
            } else {
              saveChangesOverlay.close()
              canceled()
            }
          }}
        >
          {t('edit-index.cancel.button', 'Cancel')}
        </Button>
        <Button
          data-testid="save-component-save-button"
          linkName="PanelFooterSaveButton"
          disabled={saving || disableSave}
          emphasis="high"
          type="submit"
          Icon={saving && NoticeLoading}
          form={formName}
          onClick={handleSubmitClick}
          ref={saveButtonRef}
        >
          {t('edit-index.save-changes.button', 'Save Changes')}
        </Button>
      </Row>
      <UnsavedChangesOverlay
        disableSave={saving}
        overlay={saveChangesOverlay}
        onDiscard={() => {
          saveChangesOverlay.close()
          canceled()
        }}
        onSave={() => {
          saveChangesOverlay.close()
          saveButtonRef.current.click()
        }}
        formId="edit-person-form"
      />
      <ErrorOverlay
        error={error}
        setError={setError}
        title={t('save-error-overlay.heading', 'Unable to Save Changes')}
        message={t('save-error-overlay.content-generic', 'Your changes were not saved.  Please try again later.')}
      />
    </>
  )
}
